import * as _ from 'lodash';
import { AfterViewInit, Component, effect, inject, input, OnInit, signal, Signal, TemplateRef, untracked, viewChild } from '@angular/core';
import { Router } from '@angular/router';

import { ILevelGeneralDto } from '../../models/level-general-dto';
import { ITabDataTemplate } from 'src/app/features/shared/central-page/models/tab-data-template';
import { routePage, TRoutePage } from '../../services/route-service/models/route-page';
import { TabListService } from 'src/app/features/shared/central-page/services/tab-list.service';
import { NotificationService } from 'src/app/features/shared/notification/notification.service';
import { _BaseRouteComponent } from './_base-route-component';
import { injectRequired } from 'src/app/infrastructure/functions/inject-required.function';
import { TPageMode } from '../../models/page-mode';
import { NavigationHistoryService } from 'src/app/features/shared/navigation-history/services/navigation-history.service';
import { navCategoryRoutePage, navHistoryDescriptors } from 'src/app/features/shared/navigation-history/models/nav-history-descriptors';
import { IContainerTabSnapshot, INavHistoryEntry, navCategory, USER_L3_CATEGORIES } from 'src/app/features/shared/navigation-history/models/nav-history-entry';
import { ApiService } from '../../services/api-service/api.service';
import { ApplicationUserService } from '../../services/application-user.services/application-user.service';
import { CLAIM_PAGE_DETAILS } from '../../injection-tokens/PAGE-NAME';
import { claimName } from 'src/assets/common/enums/claim-name.g';
import { domainUserData } from 'src/app/features/domain-user/models/domain-user-data';
import { domainData, IDomainData } from 'src/app/features/domain/models/domain-data';
import { projectMemberData } from 'src/app/features/project-member/models/project-member-data';
import { projectData, IProjectData } from 'src/app/features/project/models/project-data';
import { PageData } from '../../services/page-services/models/page-data';

@Component({
    selector: 'app-tab-page',
    template: '',
    styles: [],
})
export abstract class _TabPageRouteComponent<DtoModel extends ILevelGeneralDto | null, FormModel>
    extends _BaseRouteComponent
    implements OnInit, AfterViewInit
{
    abstract readonly tabListService: TabListService;
    abstract readonly notificationService: NotificationService;
    abstract readonly apiService: ApiService;

    abstract pageCreate_name: TRoutePage | null;
    abstract pageMode_initial: TPageMode;
    abstract photo_show: boolean;

    abstract sourceList$$: () => ITabDataTemplate[];
    abstract template_compact$$: () => TemplateRef<any> | undefined;

    abstract emit_onEscape(): void;
    abstract ngAfterViewInit(): void;
    abstract ngOnInit(): void;

    protected readonly _notificationService = injectRequired(NotificationService);
    protected readonly _tabListService = injectRequired(TabListService);
    protected readonly _apiService = injectRequired(ApiService);
    

    // Protected (niet __private): concrete pagina's (ProjectComponent e.a.) patchen zelf ook
    // nav-history-entries in-place (bv. photoGuid_update na een mainphoto-wijziging buiten een
    // nieuwe record()-navigatie om) en hergebruiken daarvoor deze ene geïnjecteerde instantie i.p.v.
    // 'm nogmaals te injecteren (zie feedback_di_services_visible).
    protected readonly _navHistoryService = inject(NavigationHistoryService);
    private readonly __appUserService = injectRequired(ApplicationUserService);
    // Optioneel: alleen gezet als de concrete container 'm zelf provide't (bv. DomainComponent).
    // Zie _ngOnInit hieronder. Los token t.o.v. CLAIM_PAGE — zie CLAIM_PAGE_DETAILS-comment in
    // PAGE-NAME.ts voor waarom dit niet via CLAIM_PAGE zelf mag lopen.
    private readonly __claimPage = inject(CLAIM_PAGE_DETAILS, { optional: true });

    resolver$$ = input.required<any>();
    
    protected _template_compact_default$$ = viewChild<TemplateRef<any>>('template_compact');

    constructor() {
        super();

        effect(() => this.tabListService.template_sectionCompact_set(this.template_compact$$()));

        // Na de eerste toewijzing in _ngOnInit: een latere wijziging van de tab-SET (andere namen,
        // bv. Home na de async domain-check: onboarding / geen actief domain) alsnog doorzetten.
        // Alleen bij andere namen, zodat een herberekening met dezelfde tabs de lijst niet opnieuw opbouwt.
        effect(() => {
            const __sourceList = this.sourceList$$();
            if (!this.__sourceListAssigned$$()) return;

            untracked(() => {
                const __names = (list: ITabDataTemplate[]) => list.map(t => t.name);
                if (_.isEqual(__names(__sourceList), __names(this.tabListService.SourceList))) return;
                this.tabListService.SourceList = __sourceList;
            });
        });
    }

    private __sourceListAssigned$$ = signal<boolean>(false);

    protected override _ngOnInit(): void {
        super._ngOnInit();

        // Vroege auth-pagina-registratie: normaal registreert de concrete XxxDetailsComponent zichzelf
        // pas zodra hij mount (_SectionDetailsComponent._ngOnInit), maar die mount pas zodra de
        // gebruiker naar de details-stand wisselt — in tab-page-modus (de standaard bij page-load)
        // dus nooit vanzelf. Zonder deze registratie blijft _CentralPageService.pageName_sectionDetails$$
        // null en verbergt __sectionButton_setAuth de Edit/Delete-knoppen in de tab-footer standaard,
        // ook als de gebruiker er wél recht op heeft. Idempotent: DomainDetailsComponent registreert
        // zodra hij alsnog mount dezelfde waarde nogmaals (zie diens skipSelf-CLAIM_PAGE-provider).
        if (this.__claimPage) this._centralPageService.pageName_sectionDetails_set(this.__claimPage);

        this.tabListService.photoShow_set(this.photo_show);

        if (!!this._formModelService) {
            this._formModelService.dtoModel_set(this.resolver$$());
        }
        this.tabListService.SourceList = this.sourceList$$();
        this.__sourceListAssigned$$.set(true);

        this.__navHistory_recordTabs();
        this.__navHistory_record(this.resolver$$());
    }

    protected override _emit_onEscape(): void {
        // Escape keert terug naar de laatst geregistreerde zichtbare stand (bv. medium-page bij
        // Theme) — gelijk aan footer-swipe-omhoog en de show-toggle. Resetten naar de standaard
        // (tabPage) is voorbehouden aan de reset-knop.
        this.tabListService.position_restore();
    }

    // Snapshot van de geautoriseerde tabs van deze pagina t.b.v. de navigation-history-drawer, die zo dezelfde tab-lijst
    // kan tonen als de echte tabheader — inclusief tabs zonder bezochte items. Zelfde autorisatie-regel als
    // TabListService.__sourceList_checkAuth, maar hier synchroon berekend i.p.v. te wachten op diens eigen (async)
    // autorisatie-effect. Containers (Domain/Project/AdminPanel) onder hun eigen sleutel; elke andere pagina met een
    // nav-history-descriptor (Taak, Melding, Contact, ...) onder `item:{guid}` (NavigationHistoryService ruimt die op
    // zodra de pagina uit de geschiedenis valt).
    private __navHistory_recordTabs(): void {
        const __isContainer = this.pageCurrent_name === routePage.domain
            || this.pageCurrent_name === routePage.project
            || this.pageCurrent_name === routePage.adminPanel;
        if (!__isContainer && !navHistoryDescriptors[this.pageCurrent_name]) return;

        const __isAdmin = this._router.url.startsWith('/admin');
        const __guid: string | null = (this.resolver$$() as any)?.guid ?? null;
        if ((!__isAdmin || !__isContainer) && !__guid) return;

        const __routeKeys = this._activatedRouteService?.routeKeys ?? null;
        const __tabs: IContainerTabSnapshot[] = this.sourceList$$()
            .filter(t => this.__appUserService.claim_check(t.name, __routeKeys, claimName.page_Read))
            .map(t => ({ name: t.name, label: t.label ?? t.name }));

        const __key = !__isContainer ? `item:${__guid}`
            : __isAdmin ? 'admin'
            : this.pageCurrent_name === routePage.project ? `project:${__guid}`
            : `domain:${__guid}`;
        this._navHistoryService.containerTabs_record(__key, __tabs);
    }

    // Registreer dit bezoek in de navigation-history. Categorie volgt uit
    // pageCurrent_name (descriptor-registry); admin vs user wordt onderscheiden via de
    // /admin-URL (admin ⇒ null-scope, anders domein/project-scope).
    //
    // dGuid/oGuid komen NIET uit los-DTO-velden (dto.domain_Guid/dto.order_Guid) — die bestaan
    // alleen op List-DTO's, niet op de Detail-DTO's die deze pagina's resolven (sommige, zoals
    // ContactDto, hebben zelfs helemaal geen domain-referentie). In plaats daarvan leunen we op
    // ApplicationUserService.domainUserData/projectMemberData — MAAR dat is een gedeeld, sessie-breed
    // veld dat bij ELK response wordt overschreven (ResponseStateService.apply), dus niet per
    // definitie de scope van de pagina die je nu bekijkt: een trager, later binnenkomend response van
    // een inmiddels verlaten pagina kan het veld overschrijven vlak voordat dít record() draait —
    // precies de onvoorspelbaarheid die in de drawer werd waargenomen (Domain → Function → terug).
    // Daarom wordt het veld hieronder gevalideerd tegen de dauGuid/pamGuid die letterlijk in de URL
    // van DEZE pagina staan (__urlKeys, tekstueel geparsed via PageData/RouteKeyData — NIET via
    // Angular's ActivatedRoute.params: geneste detailpagina's krijgen dauGuid/pamGuid van hun
    // voorouder-segment niet automatisch mee, paramsInheritanceStrategy staat op de Angular-default
    // 'emptyOnly'). Bij een mismatch hoort het veld bij een ander bezoek en wordt de domain/project-
    // scope voor dit record overgeslagen i.p.v. foutief toegepast — een volgend (niet-racend) bezoek
    // herstelt het vanzelf, record() is een upsert.
    private __navHistory_record(dto: any): void {
        const __descriptor = navHistoryDescriptors[this.pageCurrent_name];
        const __guid: string | null = dto?.guid ?? null;
        if (!__descriptor || !__guid) return;
        const __category = __descriptor.categoryOf?.(dto) ?? __descriptor.category;

        // Function had ooit een dual-context probleem (pageRoute_goto navigeerde altijd naar
        // /admin-panel/functions/{key}, ongeacht herkomst) — sindsdien opgelost bij de bron
        // (FunctionPageService.pageRoute_goto kiest nu de geneste /domains/{dauGuid}/functions/{key}
        // -route zodra er een echte pad-dauGuid is, anders de canonieke admin-route), dus Function
        // heeft hier geen uitzondering meer nodig: de url zelf is weer betrouwbaar voor iedere pagina.
        const __isAdmin = this._router.url.startsWith('/admin');

        const __urlKeys = !__isAdmin ? PageData.create(this._router.url, this._router).routeKeyData ?? null : null;

        const __domainMatches = !!__urlKeys?.dauGuid
            && this.__appUserService.domainUserData?.[domainUserData.guid] === __urlKeys.dauGuid;
        const __projectMatches = !!__urlKeys?.pamGuid
            && this.__appUserService.projectMemberData?.[projectMemberData.guid] === __urlKeys.pamGuid;

        const __currentDomain = __domainMatches ? this.__appUserService.domainUserData?.[domainUserData.domainData] ?? null : null;
        const __currentDauGuid = __domainMatches ? this.__appUserService.domainUserData?.[domainUserData.guid] ?? null : null;
        const __currentProject = __projectMatches ? this.__appUserService.projectMemberData?.[projectMemberData.projectData] ?? null : null;
        const __currentPamGuid = __projectMatches ? this.__appUserService.projectMemberData?.[projectMemberData.guid] ?? null : null;

        const __dGuid = __category === navCategory.domains ? null : (__currentDomain?.[domainData.guid] ?? null);
        const __pGuid = USER_L3_CATEGORIES.includes(__category) ? (__currentProject?.[projectData.guid] ?? null) : null;

        // Zorg dat domein (met logo) en project altijd als root-knopen bestaan, ook als je
        // rechtstreeks doorlinkt naar een child (project/contact/notice/...) zonder ooit de
        // Domain- of Project-pagina zelf te hebben bezocht — anders heeft dat kind niets om
        // onder te nestelen en verdwijnt het stilletjes uit de boom.
        if (__currentDomain && __category !== navCategory.domains) {
            this.__domainEntry_ensure(__currentDomain, __currentDauGuid);
        }
        if (__currentProject && __category !== navCategory.projects) {
            this.__projectEntry_ensure(__currentProject, __currentDomain, __currentDauGuid, __currentPamGuid);
        }

        this._navHistoryService.record({
            category: __category,
            guid: __guid,
            label: __descriptor.label(dto) || __guid,
            photoGuid: __descriptor.photoGuid(dto),
            logoFolderShard: __descriptor.logoFolderShard(dto),
            logoVersion: __descriptor.logoVersion(dto),
            dGuid: __dGuid,
            oGuid: __pGuid,
            url: this._router.url,
            photoIsUserFallback: __descriptor.photoIsUserFallback(dto),
            email: __descriptor.email(dto),
            parentGuid: __descriptor.parent?.(dto)?.guid ?? null,
            parentCategory: __descriptor.parent?.(dto)?.category ?? null,
        });

        // Ouder (taak in een taak, melding in een taak): als entry garanderen, zodat het kind iets heeft om onder te
        // hangen. Url: van hetzelfde soort die van dit bezoek met de guid van de ouder, anders de detailroute van de
        // ouder. Zijn eigen ouder volgt bij een bezoek aan die pagina.
        const __parent = __descriptor.parent?.(dto) ?? null;
        if (__parent) {
            const __parentCategory = __parent.category ?? __category;
            const __parentPage = navCategoryRoutePage[__parentCategory];
            const __parentUrl = __parentCategory === __category
                ? this._router.url.split('?')[0].replace(__guid, __parent.guid)
                : __parentPage ? PageData.create(__parentPage, { ...__urlKeys, key: __parent.guid })?.routePath ?? null : null;
            if (__parentUrl) {
                this._navHistoryService.ensure({
                    category: __parentCategory,
                    guid: __parent.guid,
                    label: __parent.label || __parent.guid,
                    photoGuid: null,
                    logoFolderShard: null,
                    logoVersion: null,
                    dGuid: __dGuid,
                    oGuid: __pGuid,
                    url: __parentUrl,
                    parentGuid: null,
                });
            }
        }
    }

    // Geen modifiedOn beschikbaar buiten de Domain-pagina zelf ⇒ logoVersion blijft hier null
    // (geen cache-bust langs dit indirecte pad); een echt Domain-bezoek levert de precieze versie
    // en overschrijft deze entry gewoon (record() is een upsert).
    private __domainEntry_ensure(domain: IDomainData, dauGuid: string | null): void {
        this._navHistoryService.record({
            category: navCategory.domains,
            guid: domain[domainData.guid],
            label: domain[domainData.name] ?? '',
            photoGuid: domain[domainData.photoThumbGuid] ?? null,
            logoFolderShard: domain[domainData.logos_fileShardPath] ?? null,
            logoVersion: null,
            dGuid: null,
            oGuid: null,
            url: PageData.create(routePage.domain, { dauGuid })?.routePath ?? '',
        } satisfies Omit<INavHistoryEntry, 'visitedAt'>);
    }

    private __projectEntry_ensure(project: IProjectData, domain: IDomainData | null, dauGuid: string | null, pamGuid: string | null): void {
        this._navHistoryService.record({
            category: navCategory.projects,
            guid: project[projectData.guid],
            label: project[projectData.name] ?? '',
            photoGuid: project[projectData.photoThumbGuid] ?? null,
            logoFolderShard: null,
            logoVersion: null,
            dGuid: domain?.[domainData.guid] ?? null,
            oGuid: null,
            url: PageData.create(routePage.project, { dauGuid, pamGuid })?.routePath ?? '',
        } satisfies Omit<INavHistoryEntry, 'visitedAt'>);
    }

    protected _ngAfterViewInit(): void {
        setTimeout(() => {
            
        }, 0);
    }
}
